import React, { useEffect, useState } from "react";
import Icon from "@mui/material/Icon";
import Divider from "@mui/material/Divider";
import PropTypes from "prop-types";
import toast from "react-hot-toast";
import { useMaterialUIController } from "../../../context";
import useApi from "../../../components/Utils/api";
import ConfiguratorRoot from "../../../examples/Configurator/ConfiguratorRoot";
import MDBox from "../../../components/MDBox";
import MDTypography from "../../../components/MDTypography";
import MDInput from "../../../components/MDInput";
import MDButton from "../../../components/MDButton";
import { Controller, useForm, FormProvider } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { terminalSchema, terminalModel } from "../../../models/terminalSchema";
import MenuItem from "@mui/material/MenuItem";
import { Autocomplete, FormControl, InputLabel, Select } from "@mui/material";
import Grid from "@mui/material/Grid";
import OutlinedInput from "@mui/material/OutlinedInput";
import {useTranslation} from "react-i18next";
import useAllowOnlyNumber from "../../../hooks/useAllowOnlyNumber";
import useAllowOnlyLetters from "../../../hooks/useAllowOnlyLetters";
import useAllowOnlyAlphanumeric from "../../../hooks/useAllowOnlyAlphanumeric";


function NewTerminal({openConfigurator, setOpenConfigurator, terminal, merchant, onTerminalCloseHandler, refreshMerchant}) {
  const [controller] = useMaterialUIController();
  const {t} = useTranslation();
  const {user} = controller;
  const handleNumberKeyDown = useAllowOnlyNumber();
  const handleLettersKeyDown = useAllowOnlyLetters();
  const handleAlphanumericKeyDown = useAllowOnlyAlphanumeric();
  const { register, handleSubmit, reset, formState: { errors }, trigger, watch, control, setValue } = useForm({
    mode: "onChange",
    resolver: yupResolver(terminalSchema)
  });
  const isUpdate = Object.keys(terminal).length > 0;
  // Device picker (create only): active, unmapped devices searched by serial number
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [deviceSearch, setDeviceSearch] = useState("");
  const [deviceOptions, setDeviceOptions] = useState([]);
  useEffect(() => {
    console.log(merchant);
    if(Object.keys(terminal).length > 0 ){
      reset(terminal);
    } else {
      reset(terminalModel);
      setValue("mid", merchant.merchant.mid);
      setValue("msisdn", merchant.merchant.mobileNo);
      setValue("emailtid", merchant.merchant.emailId);

    }
    setSelectedDevice(null);
    setDeviceSearch("");

  }, [openConfigurator])
  const api = useApi();
  useEffect(() => {
    if (!openConfigurator || isUpdate) return undefined;
    const timer = setTimeout(() => {
      api.get("device/available", { params: { serialNumber: deviceSearch } })
        .then((response) => setDeviceOptions(Array.isArray(response.data) ? response.data : []))
        .catch(() => setDeviceOptions([]));
    }, 300);
    return () => clearTimeout(timer);
  }, [deviceSearch, openConfigurator]);
  const handleDeviceChange = (event, device) => {
    setSelectedDevice(device);
    setValue("deviceType", device ? device.deviceType : "", { shouldValidate: true });
  };
  // Botim terminal keys (update only): copy when registered, otherwise allow adding them
  const [botimKeys, setBotimKeys] = useState(null);
  const [keysForm, setKeysForm] = useState({ deviceSerialNumber: "", privateKey: "", publicKey: "" });
  const [savingKeys, setSavingKeys] = useState(false);
  const loadBotimKeys = () => {
    api.get("terminal/botimKeys", { params: { tid: terminal.tid } })
      .then((response) => setBotimKeys(response.data))
      .catch(() => setBotimKeys({ exists: false }));
  };
  useEffect(() => {
    if (!openConfigurator || !isUpdate) return;
    setBotimKeys(null);
    setKeysForm({ deviceSerialNumber: terminal.serialNumber || "", privateKey: "", publicKey: "" });
    loadBotimKeys();
  }, [openConfigurator, terminal]);
  const copyKey = (value, label) => {
    navigator.clipboard.writeText(value || "")
      .then(() => toast.success(t('botimKeys.copied', { key: label })))
      .catch(() => toast.error(t('botimKeys.copyFailed')));
  };
  const saveBotimKeys = () => {
    const payload = {
      tid: terminal.tid,
      deviceSerialNumber: keysForm.deviceSerialNumber.trim(),
      privateKey: keysForm.privateKey.trim(),
      publicKey: keysForm.publicKey.trim(),
    };
    if (!payload.deviceSerialNumber || !payload.privateKey || !payload.publicKey) {
      toast.error(t('botimKeys.allRequired'));
      return;
    }
    setSavingKeys(true);
    api.post("terminal/botimKeys", payload)
      .then(() => {
        toast.success(t('botimKeys.saved'));
        loadBotimKeys();
      })
      .catch((error) => {
        const data = error.response?.data;
        toast.error(Array.isArray(data?.errors) ? data.errors.join("\n") : data?.message || t('something_went_wrong'));
      })
      .finally(() => setSavingKeys(false));
  };
  const createTerminalHandler = (data) => {
    data.deviceSerialNumber = selectedDevice ? selectedDevice.serialNumber : null;
    api.post("terminal/create", data)
      .then((response) => {
        if(response.data.statusCodeValue === 200){
          toast.success(t('terminal_created'));
          setOpenConfigurator(false);
          refreshMerchant();
        }
      })
      .catch((error) => {
        if (error.response && error.response.status === 403) {
          toast.error(t('you_do_not_have_permission_to_perform_this_action'), { id: "PermissionError" });
          return;
        }
        console.log(error.response)
        toast.error(error.response.data.body)
      })
  }
  function handleCityChange(e) {
    const state = e.explicitOriginalTarget.getAttribute('data-state');
    const country = e.explicitOriginalTarget.getAttribute('data-country');
    setValue("state", state);
    setValue("country", country);
  }
  function updateTerminalHandler(data) {
    api.post("terminal/update", data)
      .then(response => {
        if(response.data.statusCodeValue === 200) {
          toast.success(t('terminal_successfully_updated'));
          reset(terminalModel);
          setOpenConfigurator(false);
          refreshMerchant();
        }
      })
      .catch(error => {
        console.log(error);
      });
  }
  function submitHandler() {
    if(Object.keys(terminal).length === 0 ){
      handleSubmit(createTerminalHandler)();
    } else {
      handleSubmit(updateTerminalHandler)();
    }
  }

  return (
    <>
      {openConfigurator && (
        <MDBox
          onClick={() => {
            setOpenConfigurator(false);
            onTerminalCloseHandler();
          }}
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            zIndex: (theme) => theme.zIndex.drawer - 1,
          }}
        />
      )}
      <ConfiguratorRoot variant="permanent" ownerState={{ openConfigurator }} configuratorwidth={500}>
      <MDBox
        display="flex"
        justifyContent="space-between"
        alignItems="baseline"
        pt={4}
        pb={0.5}
        px={3}
      >
        <MDBox>
          <MDTypography variant="h5">{Object.keys(terminal).length > 0 ? t('update') : t('new')} {t('terminal')}</MDTypography>
          {/*<MDTypography variant="body2" color="text">*/}
          {/*  {updatedUser?.userId}*/}
          {/*</MDTypography>*/}
        </MDBox>

        <Icon
          sx={({ typography: { size }, palette: { dark, white } }) => ({
            fontSize: `${size.lg} !important`,
            color: dark.main,
            stroke: "currentColor",
            strokeWidth: "2px",
            cursor: "pointer",
            transform: "translateY(5px)",
          })}
          onClick={() => {
            setOpenConfigurator(false);
            onTerminalCloseHandler();
            }
          }
        >
          close
        </Icon>
      </MDBox>
      <Divider />
      <Grid container spacing={2} justifyContent="center">
        <FormProvider {...{ register, handleSubmit, reset, formState: { errors }, trigger, watch, control }}>
          <Grid item xs={6}>
            <MDInput
              type="text" label={t('terminalTablecolumns.mid')} name="mid"
              error={errors.mid}
              helperText={errors.mid ? errors.mid.message : ''}
              fullWidth
              disabled
              {...register("mid")}
            />
          </Grid>

          <Grid item xs={6}>
            <MDInput type="text" label={t('terminalTablecolumns.msisdn')} name="msisdn"
                     error={errors.msisdn}
                     helperText={errors.msisdn ? errors.msisdn.message : ''}
                     fullWidth
                     required
                     onKeyDown={handleNumberKeyDown}
                     inputProps={{ maxLength: 12 }}
                     {...register("msisdn")}
            />
          </Grid>

          <Grid item xs={6}>
            <MDInput type="text" label={t('terminalTablecolumns.tid_username')} name="tid"
                     error={errors.tid}
                     helperText={errors.tid ? errors.tid.message : ''}
                     fullWidth
                     required
                     disabled={Object.keys(terminal).length > 0}
                     onKeyDown={handleAlphanumericKeyDown}
                     inputProps={{ maxLength: 8 }}
                     {...register("tid")}
            />
          </Grid>

          <Grid item xs={6}>
            <MDInput type="text" label={t('terminalTablecolumns.name')} name="name"
                     error={errors.name}
                     helperText={errors.name ? errors.name.message : ''}
                     fullWidth
                     onKeyDown={handleLettersKeyDown}
                     {...register("name")}
            />
          </Grid>

          {/*<Grid item xs={6}>*/}
          {/*  <FormControl fullWidth style={{height: '100%'}}>*/}
          {/*    <InputLabel>City</InputLabel>*/}
          {/*    <Controller*/}
          {/*      name="city"*/}
          {/*      control={control}*/}
          {/*      defaultValue=""*/}
          {/*      render={({ field }) => (*/}
          {/*        <Select*/}
          {/*          {...field}*/}
          {/*          style={{height: '100%'}}*/}
          {/*          onChange={(e) => {*/}
          {/*            field.onChange(e); // Default handler for react-hook-form*/}
          {/*            handleCityChange(e);*/}
          {/*          }}*/}
          {/*          input={<OutlinedInput label="City" />}*/}
          {/*        >*/}
          {/*          <MenuItem value="-1">- Select City -</MenuItem>*/}
          {/*          {merchant?.cityList.map(city => (*/}
          {/*            <MenuItem data-state={city.state} data-country={city.country} key={city.cityId} value={city.cityId}>{city.cityCode}</MenuItem>*/}
          {/*          ))}*/}
          {/*        </Select>*/}
          {/*      )}*/}
          {/*    />*/}
          {/*  </FormControl>*/}
          {/*</Grid>*/}

          {/*<Grid item xs={6}>*/}
          {/*  <MDInput type="text" label="State" name="state"*/}
          {/*           error={errors.state}*/}
          {/*           helperText={errors.state ? errors.state.message : ''}*/}
          {/*           fullWidth*/}
          {/*           disabled*/}
          {/*           {...register("state")}*/}
          {/*  />*/}
          {/*</Grid>*/}

          {/*<Grid item xs={6}>*/}
          {/*  <MDInput type="text" label="Country" name="country"*/}
          {/*           error={errors.country}*/}
          {/*           helperText={errors.country ? errors.country.message : ''}*/}
          {/*           fullWidth*/}
          {/*           disabled*/}
          {/*           {...register("country")}*/}
          {/*  />*/}
          {/*</Grid>*/}

          <Grid item xs={6}>
            <MDInput type="text" label={t('terminalTablecolumns.store_name')} name="storename"
                     error={errors.storename}
                     helperText={errors.storename ? errors.storename.message : ''}
                     fullWidth
                     onKeyDown={handleAlphanumericKeyDown}
                     {...register("storename")}
            />
          </Grid>

          <Grid item xs={6}>
            <MDInput type="text" label={t('terminalTablecolumns.store_address')} name="storeaddress"
                     error={errors.storeaddress}
                     helperText={errors.storeaddress ? errors.storeaddress.message : ''}
                     fullWidth
                     {...register("storeaddress")}
            />
          </Grid>

          <Grid item xs={6}>
            <MDInput type="email" label={t('terminalTablecolumns.email')} name="emailtid"
                     error={errors.emailtid}
                     helperText={errors.emailtid ? errors.emailtid.message : ''}
                     fullWidth
                     {...register("emailtid")}
            />
          </Grid>

          {!isUpdate ? (
          <Grid item xs={12}>
            <Autocomplete
              options={deviceOptions}
              value={selectedDevice}
              onChange={handleDeviceChange}
              inputValue={deviceSearch}
              onInputChange={(event, value) => setDeviceSearch(value)}
              filterOptions={(options) => options}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              getOptionLabel={(option) => `${option.serialNumber} — ${option.deviceName} (${option.deviceType})`}
              noOptionsText={t('devicePage.noAvailableDevices')}
              renderInput={(params) => (
                <MDInput
                  {...params}
                  label={t('devicePage.selectDeviceBySerial')}
                  required
                  error={!!errors.deviceType}
                  helperText={errors.deviceType ? t('devicePage.deviceRequired') : ''}
                />
              )}
            />
          </Grid>
          ) : (
          <>
          <Grid item xs={6}>
            <MDInput type="text" label={t('terminalTablecolumns.serialNumber')}
                     value={terminal.serialNumber || "-"}
                     fullWidth
                     disabled
            />
          </Grid>
          <Grid item xs={6}>
            <FormControl fullWidth style={{height: '100%'}}>
              <InputLabel id="devicetype">{t('terminalTablecolumns.deviceType')} *</InputLabel>
              <Controller
                name="deviceType"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <Select {...field} style={{height: '45px'}} label={t('terminalTablecolumns.deviceType')} required labelId="devicetype">
                    <MenuItem value="-1">- {t('terminalTablecolumns.select_device')} -</MenuItem>
                    <MenuItem value="Y1000K">Y1000K</MenuItem>
                  </Select>
                )}
                required
              />
            </FormControl>
          </Grid>
          </>
          )}
          <Grid item xs={12}>
            <MDButton onClick={() => submitHandler()} variant="gradient" color="info" style={{float : 'right'}}>
              {Object.keys(terminal).length > 0 ? t('update') : t('add')}
            </MDButton>
          </Grid>
        </FormProvider>
        {isUpdate && (
          <Grid item xs={12} px={2}>
            <Divider />
            <MDTypography variant="h6">{t('botimKeys.title')}</MDTypography>
            {botimKeys === null ? (
              <MDTypography variant="button" color="text">{t('botimKeys.loading')}</MDTypography>
            ) : botimKeys.exists ? (
              <>
                <MDTypography variant="button" color="text" display="block" mb={1}>
                  {t('botimKeys.registeredFor', { serial: botimKeys.deviceSerialNumber })}
                </MDTypography>
                <MDBox display="flex" gap={1} flexWrap="wrap">
                  <MDButton variant="outlined" color="info" size="small" onClick={() => copyKey(botimKeys.privateKey, t('botimKeys.privateKey'))}>
                    <Icon>content_copy</Icon>&nbsp;{t('botimKeys.copyPrivateKey')}
                  </MDButton>
                  <MDButton variant="outlined" color="info" size="small" onClick={() => copyKey(botimKeys.publicKey, t('botimKeys.publicKey'))}>
                    <Icon>content_copy</Icon>&nbsp;{t('botimKeys.copyPublicKey')}
                  </MDButton>
                </MDBox>
              </>
            ) : (
              <Grid container spacing={2} mt={0}>
                <Grid item xs={12}>
                  <MDInput type="text" label={t('botimKeys.deviceSerialNumber')} fullWidth required
                           value={keysForm.deviceSerialNumber}
                           disabled={!!terminal.serialNumber}
                           inputProps={{ maxLength: 50 }}
                           onChange={(e) => setKeysForm({ ...keysForm, deviceSerialNumber: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12}>
                  <MDInput type="text" label={t('botimKeys.privateKeyPem')} fullWidth required multiline rows={4}
                           value={keysForm.privateKey}
                           onChange={(e) => setKeysForm({ ...keysForm, privateKey: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12}>
                  <MDInput type="text" label={t('botimKeys.publicKeyPem')} fullWidth required multiline rows={4}
                           value={keysForm.publicKey}
                           onChange={(e) => setKeysForm({ ...keysForm, publicKey: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12}>
                  <MDButton onClick={saveBotimKeys} variant="gradient" color="info" disabled={savingKeys} style={{float : 'right'}}>
                    {t('botimKeys.saveKeys')}
                  </MDButton>
                </Grid>
              </Grid>
            )}
          </Grid>
        )}
      </Grid>
      </ConfiguratorRoot>
    </>
  );
}

NewTerminal.defaultProps = {
  openConfigurator: false,
  setOpenConfigurator: false,
  terminal : {},
  merchant: {}
}

NewTerminal.propTypes = {
  openConfigurator: PropTypes.bool,
  setOpenConfigurator: PropTypes.func,
  terminal: PropTypes.object,
  merchant : PropTypes.object,
  onTerminalCloseHandler: PropTypes.func,
  refreshMerchant: PropTypes.func.isRequired,
}

export default NewTerminal;